import { useCallback } from 'react'

import type { MasterTransaction } from '@model/MasterTransaction'
import {
  isTransactionFormRedirect,
  isTransactionHistory,
  isTransactionSuccessful,
  isTransactionUnSuccessful,
  isTransactionUrlRedirect,
} from '@model/MasterTransaction'
import { isPaymentProviderBankType } from '@model/PaymentProviderType'

import type { DepositFormValues } from '../../DepositFlowLayout/types'

export type PendingRedirectState = {
  id: string
  tx: MasterTransaction
  values: DepositFormValues
}

const cryptoRandomId = () => {
  try {
    return crypto?.randomUUID?.() ?? String(Date.now())
  } catch {
    return String(Date.now())
  }
}

export const useDepositResultNavigation = (params: {
  values: DepositFormValues
  providerCategoryId: number
  go: (to: any, opts?: any) => void
  setPendingRedirect: (s: PendingRedirectState | null) => void
}) => {
  const { values, providerCategoryId, go, setPendingRedirect } = params

  const handleDepositResult = useCallback(
    (data: MasterTransaction, overrideValues?: DepositFormValues) => {
      const currentValues = overrideValues ?? values
      if (isTransactionHistory(data)) {
        if (currentValues.dashboardContext === 'payment-agent') {
          go('/dashboard/payment-agent/transaction-history')
          return
        }
        go('/dashboard/traders-room/transaction-history')
        return
      }

      if (isTransactionUrlRedirect(data) || isTransactionFormRedirect(data)) {
        setPendingRedirect({
          id: String(data?.id ?? cryptoRandomId()),
          tx: data,
          values: currentValues,
        })
        return
      }

      if (isTransactionUnSuccessful(data)) {
        go(`../transaction/failed`, { replace: true, state: { data, providerCategoryId } })
        return
      }

      if (
        isPaymentProviderBankType(providerCategoryId, data) ||
        isPaymentProviderBankType(providerCategoryId)
      ) {
        go(`../transaction/completed`, { state: { data, providerCategoryId } })
        return
      }

      if (isTransactionSuccessful(data)) {
        go(`../transaction/succeed`, { state: { data, providerCategoryId } })
        return
      }

      go(`../transaction/completed`, { state: { data, providerCategoryId } })
    },
    [go, providerCategoryId, setPendingRedirect, values]
  )

  return { handleDepositResult }
}
